import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { MEDIAKIT_DATA_SOURCES } from "./mediakitDataSources";
import { MEDIAKIT_IMAGENES } from "./mediakitImagenes";

// Fase B, precarga (2026-09-26): el mediakit es UNA sola página — todo se carga al abrir (al montar
// el visor), no al mostrar cada lámina. Este proveedor dispara EN PARALELO todas las consultas de
// MEDIAKIT_DATA_SOURCES y precarga (en orden de lámina) todas las imágenes de MEDIAKIT_IMAGENES.
// Las láminas leen con `useMediakitData(key)` — NUNCA consultan por su cuenta (regla 2 de la fase).
const ESTADO_INICIAL = "cargando";

function estadoInicialDatos() {
  return Object.fromEntries(
    Object.keys(MEDIAKIT_DATA_SOURCES).map((key) => [key, { status: ESTADO_INICIAL, data: null }])
  );
}

const MediakitDataContext = createContext(estadoInicialDatos());

export function MediakitDataProvider({ children }) {
  const [estado, setEstado] = useState(estadoInicialDatos);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;

    Object.entries(MEDIAKIT_DATA_SOURCES).forEach(([key, fetcher]) => {
      fetcher()
        .then((data) => {
          if (montado.current) setEstado((prev) => ({ ...prev, [key]: { status: "listo", data } }));
        })
        .catch(() => {
          if (montado.current) setEstado((prev) => ({ ...prev, [key]: { status: "error", data: null } }));
        });
    });

    // Precarga de imágenes (en orden de lámina) — el navegador las cachea; cuando el <img> real de
    // cada lámina las referencia, ya están listas (nunca aparecen a medio cargar).
    MEDIAKIT_IMAGENES.forEach((src) => {
      const img = new window.Image();
      img.src = src;
    });

    return () => {
      montado.current = false;
    };
  }, []);

  return <MediakitDataContext.Provider value={estado}>{children}</MediakitDataContext.Provider>;
}

// { status: 'cargando' | 'listo' | 'error', data }. Si `key` no existe en MEDIAKIT_DATA_SOURCES
// devuelve 'cargando' con data null en vez de romper (no debería pasar si la lámina está bien
// escrita, pero nunca lanza fallo silencioso ni bloquea el render).
export function useMediakitData(key) {
  const estado = useContext(MediakitDataContext);
  return estado[key] || { status: ESTADO_INICIAL, data: null };
}

export default MediakitDataProvider;
